import { registerSW } from "virtual:pwa-register";
import { toast } from "sonner";
import i18n from "./i18n";
import { hasUnsavedReleaseChanges } from "./lib/releaseDirtyState";
import { startReleaseRefresh } from "./lib/releaseRefresh";

let registration: ServiceWorkerRegistration | undefined;
const releaseRefreshRef: { current?: ReturnType<typeof startReleaseRefresh> } = {};
let registrationReady = false;
let updateReady = false;

const updateServiceWorker = registerSW({
  onRegisteredSW: (_swScriptUrl, nextRegistration) => {
    registration = nextRegistration;
    if (releaseRefreshRef.current) releaseRefreshRef.current.notifyRegistrationReady();
    else registrationReady = true;
  },
  onNeedRefresh: () => {
    if (releaseRefreshRef.current) releaseRefreshRef.current.notifyUpdateReady();
    else updateReady = true;
  },
  // Reload is owned by controllerchange below so all paths share the same dirty guard.
  onNeedReload: () => undefined,
});

const releaseRefresh = startReleaseRefresh({
  currentBuildId: __BUILD_ID__,
  fetch: window.fetch.bind(window),
  getVisibilityState: () => document.visibilityState,
  onVisibilityChange: (listener) => {
    document.addEventListener("visibilitychange", listener);
    return () => document.removeEventListener("visibilitychange", listener);
  },
  onFocus: (listener) => {
    window.addEventListener("focus", listener);
    return () => window.removeEventListener("focus", listener);
  },
  getHistoryState: () => history.state,
  replaceHistoryState: (state) => history.replaceState(state, ""),
  reload: () => window.location.reload(),
  getRegistration: () => registration,
  getServiceWorkerController: () => navigator.serviceWorker?.controller ?? null,
  onControllerChange: (listener) => {
    navigator.serviceWorker?.addEventListener("controllerchange", listener);
    return () => navigator.serviceWorker?.removeEventListener("controllerchange", listener);
  },
  activateWaitingServiceWorker: () => updateServiceWorker(false),
  hasUnsavedChanges: hasUnsavedReleaseChanges,
  showUpdateAvailable: (activate) => {
    toast.info(i18n.t("common.releaseUpdate.available"), {
      id: "release-update-available",
      duration: Infinity,
      action: {
        label: i18n.t("common.releaseUpdate.action"),
        onClick: (event) => {
          if (!activate()) event.preventDefault();
        },
      },
    });
  },
});
releaseRefreshRef.current = releaseRefresh;

if (registrationReady) releaseRefresh.notifyRegistrationReady();
if (updateReady) releaseRefresh.notifyUpdateReady();
